/**
 * Unleash feature flag SDK configuration.
 *
 * Backed by the Unleash UI at https://admin.jdsimplified.com/feature-flags/.
 * Frontend evaluates flags via the proxy API; the `clientKey` is a frontend
 * token (default:production scope), which is browser-safe and CORS-allowed
 * from jdsimplified.com domains + localhost:5173/5174.
 *
 * Consumer pattern:
 *
 *   import { useFlag } from '@unleash/proxy-client-react';
 *   const enabled = useFlag('community-rollout');
 *
 * User-targeting context (Firebase UID) is pushed by `UnleashContextSync`
 * in main.tsx so per-user rollouts key off the JDS user_id (which equals
 * the Firebase UID and profiles.id).
 *
 * Defensive guard: `unleashEnabled` is the load-bearing boolean for whether
 * the SDK mounts at all. If either VITE_UNLEASH_URL or
 * VITE_UNLEASH_FRONTEND_TOKEN is missing at build time, Vite inlines them as
 * `undefined`, and the SDK throws `Error: url is required` synchronously
 * inside `<FlagProvider>` — which takes the whole React tree down. Callers
 * MUST check `unleashEnabled` before mounting `<FlagProvider>` so a missing
 * secret is a no-op (flags resolve to default), not a blank screen.
 */

import type { IConfig } from '@unleash/proxy-client-react';

const url = import.meta.env.VITE_UNLEASH_URL;
const clientKey = import.meta.env.VITE_UNLEASH_FRONTEND_TOKEN;

export const unleashEnabled: boolean = !!url && !!clientKey;

/**
 * Typed config — only safe to pass to `<FlagProvider>` when `unleashEnabled`
 * is true. The non-null assertions are validated by the boolean above.
 */
export const unleashConfig: IConfig = {
  url: url!,
  clientKey: clientKey!,
  refreshInterval: 15, // seconds — Unleash recommends 15-30
  appName: 'jds-frontend',
  environment:
    import.meta.env.MODE === 'production' ? 'production' : 'development',
};
